import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isString} from 'ramda-adjunct';

import {
  ConvertForAllVehiclesApiArg,
  UpdateInterestApiArg,
  useConvertForAllVehiclesMutation,
  useUpdateInterestMutation,
} from '@dms/api/finance';
import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {
  DmsDataGrid,
  handleApiError,
  InterestEndCaseDialog,
  interestSearchParams,
  Main,
  queryParams,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
  usePermissions,
} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function InterestOverview() {
  const navigate = useNavigate();

  const [updateInterest] = useUpdateInterestMutation();
  const [convertForAllVehicles] = useConvertForAllVehiclesMutation();

  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {handleMandatoryFieldsError, isMandatoryFieldsError} = useMandatoryFieldsErrorDialog();

  const [canCreateInterest] = usePermissions({
    permissionKeys: ['interestCreate'],
    scopes: {
      interestCreate: {
        // no need for both scopes, general creation without specified participation or branch
        participation: 'SKIP',
        branchId: 'SKIP',
      },
    },
  });

  const actionCallback: ActionCallback = ({actionKey, rowId, sourceSearchParams, refreshData}) => {
    if (!isString(rowId)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('detail', () =>
        navigate(
          composePath(interestRoutes.detail, {
            params: {id: rowId},
            isSearchParamsPreserved: false,
            queryParams: `${sourceSearchParams}&${[queryParams.COMPONENT_SECTIONS_SECTION]}=${interestSearchParams.interestDetail.overview}&${queryParams.REDIRECT_SOURCE}=interest`,
          })
        )
      )
      .with('detail_newTab', () => {
        window.open(
          composePath(interestRoutes.detail, {
            params: {id: rowId},
            isSearchParamsPreserved: false,
            queryParams: `${sourceSearchParams}&${[queryParams.COMPONENT_SECTIONS_SECTION]}=${interestSearchParams.interestDetail.overview}&${queryParams.REDIRECT_SOURCE}=interest`,
          })
        );
      })
      .with('open', () => {
        const args: UpdateInterestApiArg = {
          id: rowId,
          updateInterestRequestBody: {
            state: 'OPEN',
          },
        };
        updateInterest(args)
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('end', () =>
        openDialog(
          <InterestEndCaseDialog
            data-testid={testIds.interest.overview('endCaseDialog')}
            interestId={rowId}
            onSubmit={refreshData}
          />,
          {
            'data-testid': testIds.interest.overview('endCaseDialog'),
            title: i18n.t('entity.interest.labels.endInterestCase'),
            scrollBehavior: 'outside',
          }
        )
      )
      .with('createBusinessCase', () => {
        const args: ConvertForAllVehiclesApiArg = {
          interestId: rowId ?? '',
          validateMandatoryFields: isMandatoryFieldsEnabled(undefined),
        };
        convertForAllVehicles(args)
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch((error: ApiError) => {
            if (isMandatoryFieldsError(error)) {
              return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
            }

            return handleApiError(error);
          });
      })
      .otherwise(() => {
        showNotification.warning(`Action callback was not specified for action ${actionKey}`);
      });
  };

  const actions: Action[] = buildArray<Action>([]).when(canCreateInterest, {
    type: 'button',
    onClick: () => navigate(interestRoutes.create),
    variant: 'primary',
    'data-testid': testIds.interest.overview('create'),
    title: i18n.t('entity.interest.actions.newInterest'),
  });

  return (
    <>
      <Helmet title={i18n.t('page.interests.title')} />
      <Main isFullHeight actions={actions} data-testid={testIds.interest.overview('container')}>
        <DmsDataGrid
          data-testid={testIds.interest.overview('dataGrid')}
          gridCode="interest"
          actionCallback={actionCallback}
        />
      </Main>
    </>
  );
}
